import { useRef, ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export interface EmailSignatureHtmlEditorProps {
  /** The signature HTML being edited */
  html: string;
  /** Fired with the new HTML on every edit, and when a file is uploaded */
  onChange: (html: string) => void;
  /** Fired when an uploaded file can't be read */
  onUploadError?: () => void;
}

/**
 * Raw HTML editing for signatures the visual editor can't hold, such as a table that puts the logo
 * beside the details. The preview runs in a sandboxed iframe, so no script in the HTML runs and its
 * styles stay out of the page.
 */
export default function EmailSignatureHtmlEditor({ html, onChange, onUploadError }: EmailSignatureHtmlEditorProps) {
  const { t } = useTranslate();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Cleared so picking the same file again still fires a change
    event.target.value = '';

    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result ?? ''));
    reader.onerror = () => onUploadError?.();
    reader.readAsText(file);
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {t(
            'account.email-connection.signatures.html_hint',
            'Use a table to place your logo beside your details. Images must be web addresses or embedded in the HTML.'
          )}
        </Typography>

        <Button
          size="small"
          variant="outlined"
          startIcon={<Iconify icon="eva:upload-fill" />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0 }}
        >
          {t('account.email-connection.signatures.upload_html', 'Upload .html file')}
        </Button>

        <input ref={fileInputRef} type="file" accept=".html,.htm,text/html" hidden onChange={handleUpload} />
      </Stack>

      <TextField
        multiline
        minRows={8}
        maxRows={20}
        value={html}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('account.email-connection.signatures.html_placeholder', 'Paste your signature HTML here')}
        inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } }}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('account.email-connection.signatures.preview', 'Preview')}
        </Typography>

        <Box
          component="iframe"
          title={t('account.email-connection.signatures.preview', 'Preview')}
          sandbox=""
          srcDoc={html}
          sx={{
            width: '100%',
            height: 240,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            bgcolor: 'common.white',
          }}
        />
      </Box>
    </Stack>
  );
}
